import { gql } from '@apollo/client';
import classNames from 'classnames';
import I18n from 'i18n-js';
import { get } from 'lodash';
import React, { useState } from 'react';

import CoreLink from '@reverbdotcom/commons/src/components/core_link';
import SanitizedRender from '@reverbdotcom/commons/src/components/sanitized_render';
import { ShopBadges } from '@reverbdotcom/commons/src/components/shop_badges';
import { useUser } from '@reverbdotcom/commons/src/user_hooks';
import { RCIcon, buttonClassName, RCAlertBox, RCTextWithIcon } from '@reverbdotcom/cadence/components';
import { CommentOIcon, MagicIcon, CartIcon } from '@reverbdotcom/cadence/icons/react';
import CouponIcon from '@reverbdotcom/cadence/images/nudge/coupon.svg?component';

import DEFAULT from '../../../../images/default-avatar.jpg';
import { CoreShopContainer, ShopBuyerCouponsFragment } from '../../../schemas/rql';
import URLHelpers, { adminEditUserPath, shopPromotePath } from '../../shared/url_helpers';
import FavoriteShopCTA from '../../../../src/components/favorites/favorite_shop_cta';
import ShopCampaignCouponPreview from '../../selling/shop_campaigns/ShopCampaignCouponPreview';
import { ModalDialog } from '@reverbdotcom/commons';

function OwnerLinks() {
  return (
    <ul className="mt-1">
      <li className="d-inline-block mtb-1 mlr-1">
        <CoreLink
          to={URLHelpers.shopEditPath()}
          className="button button--small"
        >
          {I18n.t('discovery.shopHeader.editShop')}
        </CoreLink>
      </li>
      <li className="d-inline-block mtb-1 mlr-1">
        <CoreLink
          to={shopPromotePath}
          className="button button--small"
        >
          {I18n.t('discovery.shopHeader.promoteShop')}
        </CoreLink>
      </li>
    </ul>
  );
}

function Placeholder() {
  const classes = classNames(
    'shop-headerv2',
    'shop-headerv2--placeholder',
    'shop-headerv2--with-banner',
  );

  return (
    <div className={classes}>
      <div className="shop-headerv2__banner" />
      <div className="shop-headerv2__main">
        <div className="shop-headerv2__main__avatar" />
        <div className="shop-headerv2__main__text" />
      </div>
    </div>
  );
}

function BadgeContainer({ shop, stacked }: { shop: CoreShopContainer.Shop, stacked: boolean }) {
  return (
    <div className="shop-headerv2__main__badges">
      <ShopBadges
        preferredSeller={shop.preferredSeller}
        quickResponder={shop.quickResponder}
        quickShipper={shop.quickShipper}
        stacked={stacked}
      />
    </div>
  );
}

function BuyerCoupon({ coupon }: { coupon: ShopBuyerCouponsFragment.BuyerCoupons, shopName: string }) {
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  return (
    <>
      <span className="shop-headerv2__buyer-coupon-callout">
        <RCTextWithIcon svgComponent={CouponIcon} placement="left">
          <span className="shop-headerv2__buyer-coupon-available">
            {I18n.t('discovery.shopCoupons.couponAvailable', { discountValue: coupon.discountValue })}
          </span>
          <button type="button" className="shop-headerv2__buyer-coupon-modal-trigger"
            onClick={() => setShowDetailsModal(true)}>
            {I18n.t('discovery.shopCoupons.seeDetails')}
          </button>
        </RCTextWithIcon>
      </span>
      <ModalDialog
        isOpen={showDetailsModal}
        headerTitle={I18n.t('discovery.shopCoupons.couponDetails')}
        onSubmit={() => setShowDetailsModal(false)}
        onRequestClose={() => setShowDetailsModal(false)}
        saveButtonText={I18n.t('discovery.shopCoupons.gotIt')}
        hideFooterDismissButton
      >
        <ShopCampaignCouponPreview headerText={coupon.couponHeader}>
          <p>{coupon.descriptionSummary}</p>
          <p>{coupon.restrictionsSummary}</p>
        </ShopCampaignCouponPreview>
      </ModalDialog>
    </>
  );
}

interface IProps {
  shop: CoreShopContainer.Shop;
  buyerCoupon: ShopBuyerCouponsFragment.BuyerCoupons | null;
  initialFavoriteId: string | null;
}

export const ShopHeaderFragment = gql`
  fragment ShopHeaderFragment on Shop {
    _id
    id
    slug
    name
    preferredSeller
    quickShipper
    quickResponder
    description
    userUuid
    userId
    address {
      _id
      displayLocation
    }
    banner {
      source
    }
    user {
      _id
      id
      avatar {
        source
      }
    }
  }
`;

export function ShopHeader({ shop, initialFavoriteId, buyerCoupon }: IProps) {
  const user = useUser();

  const shouldRenderFreeShippingCallout = !!shop?.freeDomesticShippingRate && shop?.freeDomesticShippingRate?.regionCode === user.shippingRegionCode;
  const shouldRenderOwnerLinks = `${user.id}` === shop?.userId;
  const avatarUrl = get(shop, 'user.avatar.source', false);
  const bannerUrl = get(shop, 'banner.source');

  if (!shop) return (<Placeholder />);

  const classes = classNames(
    'shop-headerv2',
    { 'shop-headerv2--with-banner': !!bannerUrl },
  );

  return (
    <div className={classes}>
      {bannerUrl && (
        <div className="shop-headerv2__banner">
          <img className="shop-headerv2__banner__bg" src={bannerUrl} alt={shop.name} />
          <div className="shop-headerv2__banner__blur" />
          <img className="shop-headerv2__banner__main" src={bannerUrl} alt={shop.name} />
        </div>
      )}
      <div className="shop-headerv2__main">
        <div className="shop-headerv2__main__avatar">
          <img src={avatarUrl || DEFAULT} alt={shop.name} />
        </div>
        <div className="shop-headerv2__main__text">
          <h1 className="shop-headerv2__title">
            <span>
              {shop.name}
              {user.isAdmin && (
                <a
                  href={adminEditUserPath.expand({ id: shop.userId })}
                  data-edit-link
                  className="shop-headerv2__admin-link"
                >
                  <RCIcon svgComponent={() => <MagicIcon />} inline />
                </a>
              )}
            </span>
            <FavoriteShopCTA
              shop={shop}
              initialFavoriteId={initialFavoriteId}
              parentComponentName="ShopHeader"
              showCallout
            />

          </h1>
          <ul className="shop-headerv2__details">
            <li>
              <span className="icon-l-map-marker">
                {shop.address.displayLocation}
              </span>
            </li>
            <li>
              <a
                href={URLHelpers.newDashboardMessagesPath(null, shop.userUuid)}
                className={buttonClassName({ variant: 'muted', theme: 'main', size: 'mini' })}
              >
                <span className="mr-2">
                  <RCIcon svgComponent={() => <CommentOIcon />} inline />
                </span>
                {I18n.t('discovery.shopHeader.message')}
              </a>
            </li>
          </ul>
          <div className="shop-headerv2__bio">
            <SanitizedRender
              html={shop.description}
              truncationLimit={200}
              htmlTag="div"
              convertNewlines
            />
          </div>
          {buyerCoupon && (
            <div className="shop-headerV2__buyer-coupons">
              <BuyerCoupon coupon={buyerCoupon} shopName={shop.name} />
            </div>
          )}
          <>
            {shouldRenderFreeShippingCallout && (
              <div className="shop-headerv2__free-shipping-alert">
                <RCAlertBox type="success" inline>
                  <RCTextWithIcon
                    svgComponent={CartIcon}
                    placement="left"
                    size="large"
                  >
                    <SanitizedRender
                      html={I18n.t('discovery.shopHeader.freeShipping', {
                        tagOpen: '<strong>',
                        tagClose: '</strong>',
                        shopName: shop.name,
                        displayAmount: shop.freeDomesticShippingRate.freeShippingThreshold.display,
                      })
                      }
                    />
                  </RCTextWithIcon>
                </RCAlertBox>
              </div>
            )}
            {shouldRenderOwnerLinks && <OwnerLinks />}
          </>
        </div>
        <BadgeContainer shop={shop} stacked />
      </div>
    </div>
  );
}

export default ShopHeader;
